<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import BaseDropdown from '../ui/BaseDropdown.vue';
import BaseSwitch from '../ui/BaseSwitch.vue';
import type { FilterItem, SortOptionItem } from '@/types/FilterBar.ts';
import { SortOption } from '@/stores/productListFilters.ts';
import { $t } from '@/composables/translations/useTranslate.ts';

interface FilterBarProps {
  activeFilters?: FilterItem[];
  resultCount?: number;
  sortOptions?: SortOptionItem[];
  selectedSort?: SortOption;
  fixedBookableOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
  labels?: {
    filter?: string;
    resetFilters?: string;
    results?: string;
    result?: string;
    sortBy?: string;
    noFilters?: string;
  };
  wrapperClass?: string;
  filterButtonClass?: string;
  resetButtonClass?: string;
  badgeClass?: string;
  sortDropdownClass?: string;
}

const props = withDefaults(defineProps<FilterBarProps>(), {
  activeFilters: () => [],
  resultCount: 0,
  sortOptions: () => [],
  selectedSort: SortOption.RECOMMENDATION,
  fixedBookableOnly: false,
  loading: false,
  disabled: false,
  labels: () => ({}),
  wrapperClass: '',
  filterButtonClass: '',
  resetButtonClass: '',
  badgeClass: '',
  sortDropdownClass: '',
});

const emit = defineEmits<{
  'open-filters': [];
  'remove-filter': [filter: FilterItem];
  'reset-filters': [];
  'sort-change': [sortValue: SortOption];
  'update:selectedSort': [value: SortOption];
  'update:fixedBookableOnly': [value: boolean];
}>();

const currentSort = ref(props.selectedSort);

watch(
  () => props.selectedSort,
  (newValue) => {
    currentSort.value = newValue;
  }
);

const formattedSortOptions = computed(() => {
  return props.sortOptions.map((option) => ({
    value: option.value,
    label: option.label,
  }));
});

const hasActiveFilters = computed(() => props.activeFilters && props.activeFilters.length > 0);

const resetLabel = computed(() => {
  const count = props.activeFilters?.length || 0;
  return $t('ibe.filters.reset-count', 'Reset {count} filters', { count });
});

const formattedResultCount = computed(() => {
  const count = props.resultCount || 0;
  const resultWord =
    count === 1
      ? props.labels?.result || $t('ibe.filters.result', 'Result')
      : props.labels?.results || $t('ibe.filters.results', 'Results');
  return `${count} ${resultWord}`;
});

const openFilters = () => {
  if (!props.disabled && !props.loading) {
    emit('open-filters');
  }
};

const removeFilter = (filter: FilterItem) => {
  if (!props.disabled) {
    emit('remove-filter', filter);
  }
};

const resetAllFilters = () => {
  if (!props.disabled) {
    emit('reset-filters');
  }
};

const handleSortSelect = (option: { value: string | number; label: string }) => {
  if (!props.disabled) {
    const sortValue = String(option.value) as SortOption;
    currentSort.value = sortValue;
    emit('sort-change', sortValue);
    emit('update:selectedSort', sortValue);
  }
};

const handleFixedBookableChange = (value: boolean) => {
  if (!props.disabled) {
    emit('update:fixedBookableOnly', value);
  }
};
</script>

<template>
  <div :class="['w-full', wrapperClass]">
    <div class="flex justify-between items-center mb-6">
      <!-- Left section: Filter controls -->
      <div class="flex items-center gap-4">
        <button
          :disabled="disabled || loading"
          :class="[
            'bg-actions-primary px-5 py-2.5 flex items-center gap-2.5',
            'font-semibold text-base leading-6 text-white',
            'transition-colors hover:bg-actions-primary-hover',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'focus:outline-2 focus:outline-text-primary focus:outline-offset-2',
            filterButtonClass,
          ]"
          :aria-label="labels?.filter || $t('ibe.filters.filter', 'Filter')"
          :aria-expanded="false"
          @click="openFilters"
        >
          <span>{{ labels?.filter || $t('ibe.filters.filter', 'Filter') }}</span>
          <i class="w-5 h-5 bg-white icon icon-filter"></i>
        </button>

        <button
          v-if="hasActiveFilters"
          :disabled="disabled"
          :class="[
            'hidden sm:flex items-center gap-2.5 px-5 py-2.5',
            'underline font-semibold text-base',
            'transition-opacity hover:opacity-70',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'focus:outline-2 focus:outline-text-primary focus:outline-offset-2',
            resetButtonClass,
          ]"
          :aria-label="`${resetLabel}`"
          @click="resetAllFilters"
        >
          <span>{{ resetLabel }}</span>
        </button>

        <div class="hidden sm:block">
          <BaseSwitch
            id="filter-fixed-bookable-only"
            :model-value="fixedBookableOnly"
            :disabled="disabled || loading"
            :label="$t('ibe.filters.fixed-bookable-only', 'Only instantly bookable')"
            @update:model-value="handleFixedBookableChange"
          />
        </div>
      </div>

      <!-- Right section: Sorting -->
      <div v-if="sortOptions && sortOptions.length > 0" class="min-w-[148px]">
        <BaseDropdown
          v-model="currentSort"
          :options="formattedSortOptions"
          :placeholder="labels?.sortBy || $t('ibe.filters.sort-by', 'Sort by')"
          :disabled="disabled || loading"
          :class="sortDropdownClass"
          @select="handleSortSelect"
        />
      </div>
    </div>

    <!-- Mobile: switch moves below the filter button (no room on the top row) -->
    <div class="sm:hidden mb-4">
      <BaseSwitch
        id="filter-fixed-bookable-only-mobile"
        :model-value="fixedBookableOnly"
        :disabled="disabled || loading"
        :label="$t('ibe.filters.fixed-bookable-only', 'Only instantly bookable')"
        @update:model-value="handleFixedBookableChange"
      />
    </div>

    <button
      v-if="hasActiveFilters"
      :disabled="disabled"
      :class="[
        'flex sm:hidden items-center gap-2.5 py-2.5',
        'underline font-semibold text-base',
        'transition-opacity hover:opacity-70',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'focus:outline-2 focus:outline-text-primary focus:outline-offset-2',
        resetButtonClass,
      ]"
      :aria-label="`${resetLabel}`"
      @click="resetAllFilters"
    >
      <span>{{ resetLabel }}</span>
    </button>

    <!-- Active filters display -->
    <div v-if="hasActiveFilters" class="flex flex-wrap gap-1.5" role="list">
      <div
        v-for="filter in activeFilters"
        :key="filter.id"
        :class="[
          'bg-white border border-actions-tertiary pl-4 py-1.5 pr-3',
          'flex items-center gap-3 text-xs',
          'transition-colors hover:bg-actions-tertiary/10',
          badgeClass,
        ]"
        role="listitem"
      >
        <span>{{ filter.label }}</span>
        <button
          :disabled="disabled"
          :aria-label="
            $t('ibe.filters.remove-filter', 'Remove filter: {label}', { label: filter.label })
          "
          class="hover:opacity-70 transition-opacity disabled:cursor-not-allowed flex items-center justify-center focus:outline-2 focus:outline-text-primary focus:outline-offset-2"
          @click="removeFilter(filter)"
        >
          <i class="w-3 h-3 bg-black icon icon-x"></i>
        </button>
      </div>
    </div>

    <!-- Result count -->
    <div class="mt-6 text-sm tracking-wide">
      {{ formattedResultCount }}
    </div>
  </div>
</template>
